// Component: NotificationBell
// Purpose: Notification bell icon with unread badge in the Navbar.
//          Fetches unread count on mount and on window focus-return.
//          Toggles NotificationDropdown on click; loads full list when opened.
//          Reconnect / focus resume: treated as fresh REST load per project_tab_resume_refresh_parked.md
//! live-exit-lane R7.4/C1 (Tranche-B integration fix, 2026-07-24) — WS push-primary, poll-secondary.
//  Tranche A landed the BACKEND half of this channel (EscalationDispatcher._notify_user's
//  `user:{user_id}:notifications` publish + the redis_bridge.py `user:(.+):notifications` ->
//  `user.{}.notifications` mapping, commit 043f670a) but never actually wired a frontend
//  subscriber — 02-design.md's own wave plan (§7 W3) explicitly required BOTH halves land
//  together ("landing the channel without the FE subscriber, or vice versa, would be exactly
//  the hollow-green class this fix exists to close"), so notifications were reaching WS-bridged
//  Redis but arriving at zero browsers. Fixed here: subscribes to `user.{userId}.notifications`
//  via the shared WebSocketManager singleton (mirrors StrategyBuilder.jsx's
//  `user.${userId}.backtest` subscription idiom exactly — subscribeGlobal(topic, {onData}),
//  cleanup via the returned unsubscribe fn). REST-poll-on-focus (below) is RETAINED as the
//  reconnect-gap backstop, never removed — push-primary, poll-secondary per the design.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiBell } from 'react-icons/fi';
import { getNotifications } from '../../utils/notificationApi';
import NotificationDropdown from './NotificationDropdown';
import { useGlobalWebSocket, useWebSocketUser } from '../../context/WebSocketProvider';

// --> Component

const NotificationBell = () => {

  //! State — unread count (badge source-of-truth)
  const [unreadCount, setUnreadCount] = useState(0);

  // --> State — dropdown
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState(null);

  const bellRef = useRef(null);
  //! live-exit-lane R7.4/C1 — ref mirror of isOpen so the WS push handler (a stable closure
  //!  captured once by subscribeGlobal, not re-subscribed on every isOpen toggle) always reads
  //!  the CURRENT open/closed state rather than the value at subscribe-time.
  const isOpenRef = useRef(false);

  //! live-exit-lane R7.4/C1 — WS push subscription inputs.
  const { subscribeGlobal, isWsReady } = useGlobalWebSocket();
  const { userId } = useWebSocketUser();

  // --> Helpers

  /** Fetch the unread count without loading the full list. */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await getNotifications({ unread: true, limit: 1 });
      //! Badge value: use the total field from pagination envelope
      const total = (data && typeof data.total === 'number') ? data.total : 0;
      setUnreadCount(total);
      console.log('[DIAG] NotificationBell unread count', { total });
    } catch (err) {
      // Non-blocking — badge stays at last known value
      console.log('[DIAG] NotificationBell fetchUnreadCount error', { err: err.message });
    }
  }, []);

  /** Load the full notifications list into the dropdown. */
  const fetchItems = useCallback(async () => {
    setLoadingItems(true);
    setItemsError(null);
    try {
      const data = await getNotifications({ limit: 20, offset: 0 });
      setItems((data && Array.isArray(data.items)) ? data.items : []);
    } catch (err) {
      setItemsError('Failed to load notifications.');
      console.log('[DIAG] NotificationBell fetchItems error', { err: err.message });
    } finally {
      setLoadingItems(false);
    }
  }, []);

  // --> Effects

  /** On mount: fetch unread count. */
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  /**
   * Tab-resume handler — treat window focus after background as fresh REST load
   * per project_tab_resume_refresh_parked.md.
   */
  useEffect(() => {
    const handleFocus = () => {
      console.log('[DIAG] NotificationBell window focus — refreshing unread count');
      fetchUnreadCount();
      // If the dropdown is open at resume, also refresh the list.
      if (isOpen) {
        fetchItems();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isOpen, fetchUnreadCount, fetchItems]);

  /** Close dropdown when clicking outside the bell container. */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setIsOpen(false);
        isOpenRef.current = false; //! live-exit-lane R7.4/C1 — keep the WS handler's ref in sync
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * live-exit-lane R7.4/C1 — WS push subscription to `user.{userId}.notifications`.
   * On receipt: increments unreadCount immediately (badge updates WITHOUT a REST round trip —
   * this is the assertion C1's own test proves, "push-primary, not poll-disguised-as-push"),
   * and — if the dropdown is already open — prepends a locally-synthesized item so an open
   * dropdown reflects the push live too. The pub/sub envelope
   * (escalation_dispatcher.py:_notify_user, event="notification") carries no durable-row `id`
   * (that's assigned server-side by the SEPARATE HTTP-branch POST that races this publish) — a
   * client-local `dedup_key`-or-timestamp-derived id is synthesized ONLY for React key / local
   * mark-read bookkeeping; the next REST reload (focus-resume, below) reconciles against the
   * server's real durable rows as the source of truth, same convergence NotificationDropdown's
   * markRead/markAll already assume for locally-optimistic state.
   */
  useEffect(() => {
    if (!userId || !isWsReady) return;

    const topic = `user.${userId}.notifications`;

    const handleNotificationPush = (payload) => {
      if (!payload || payload.event !== 'notification') return;
      console.log('[DIAG] NotificationBell WS push received', {
        category: payload.category, severity: payload.severity,
      });
      setUnreadCount((prev) => prev + 1);
      if (isOpenRef.current) {
        setItems((prev) => [
          {
            id: payload.dedup_key || `ws-${payload.timestamp || Date.now()}`,
            title: payload.title,
            body: payload.body,
            created_at: payload.timestamp || new Date().toISOString(),
            read_at: null,
          },
          ...prev,
        ]);
      }
    };

    const unsub = subscribeGlobal(topic, { onData: handleNotificationPush });
    return () => { if (unsub) unsub(); };
  }, [userId, isWsReady, subscribeGlobal]);

  // --> Handlers

  const handleBellClick = () => {
    const next = !isOpen;
    setIsOpen(next);
    isOpenRef.current = next; //! live-exit-lane R7.4/C1 — keep the WS handler's ref in sync
    if (next) {
      // Load full list each time dropdown is opened
      fetchItems();
    }
  };

  /**
   * Called by NotificationDropdown when a single item is marked read.
   * Decrements badge and updates the item's read_at in place.
   */
  const handleMarkRead = useCallback((id) => {
    setUnreadCount((prev) => Math.max(0, prev - 1));
    setItems((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      )
    );
  }, []);

  /**
   * Called by NotificationDropdown after mark-all-read succeeds.
   * Clears badge and marks every item as read locally.
   */
  const handleMarkAll = useCallback(() => {
    setUnreadCount(0);
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
  }, []);

  // --> Render

  return (
    <div className="relative" ref={bellRef}>
      <button
        type="button"
        onClick={handleBellClick}
        className="relative text-xl rounded-full p-2 hover:bg-light-gray dark:hover:bg-bg-3 transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <FiBell className="text-fg-2" />

        {/* Unread badge — only rendered when count > 0 */}
        {unreadCount > 0 && (
          <span
            className="absolute top-1 right-1 flex items-center justify-center rounded-full bg-red-500 text-white"
            style={{ minWidth: '16px', height: '16px', fontSize: '10px', lineHeight: '16px', padding: '0 3px' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        //* NotificationDropdown: lifted state — badge updates flow back up via callbacks
        <NotificationDropdown
          items={items}
          onMarkRead={handleMarkRead}
          onMarkAll={handleMarkAll}
          loading={loadingItems}
          error={itemsError}
        />
      )}
    </div>
  );
};

export default NotificationBell;
